import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { clubAPI, dashboardAPI } from '../services/api';
import CardCapture from './CardCapture';
import './ClubFund.css';

function ClubFund() {
  const { clubId } = useParams();
  const [club, setClub] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Deposit form
  const [depositAmount, setDepositAmount] = useState('');
  const [depositWallet, setDepositWallet] = useState('kitty');
  const [depositing, setDepositing] = useState(false);
  const [depositResult, setDepositResult] = useState(null);

  // Transfer form
  const [transferAmount, setTransferAmount] = useState('');
  const [transferFrom, setTransferFrom] = useState('kitty');
  const [transferTo, setTransferTo] = useState('betting');
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);

  const [activeTab, setActiveTab] = useState('deposit');

  useEffect(() => {
    loadClubData();
  }, [clubId]);

  const loadClubData = async () => {
    try {
      setLoading(true);
      const data = await dashboardAPI.getClubDashboard(clubId);
      setClub(data.club);
      setAccounts(data.accounts);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load club data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    setDepositing(true);
    setDepositResult(null);

    try {
      const result = await clubAPI.fundClub(clubId, parseFloat(depositAmount), depositWallet);
      setDepositResult({ success: true, message: result.message });
      setDepositAmount('');
      loadClubData(); // Refresh balances
    } catch (err) {
      setDepositResult({
        success: false,
        message: err.response?.data?.error || 'Deposit failed',
      });
    } finally {
      setDepositing(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    setTransferring(true);
    setTransferResult(null);

    try {
      const result = await clubAPI.transferFunds(
        clubId,
        parseFloat(transferAmount),
        transferFrom,
        transferTo
      );
      setTransferResult({ success: true, message: result.message });
      setTransferAmount('');
      loadClubData();
    } catch (err) {
      setTransferResult({
        success: false,
        message: err.response?.data?.error || 'Transfer failed',
      });
    } finally {
      setTransferring(false);
    }
  };

  const formatCurrency = (cents) => {
    if (cents == null) return '$0.00';
    return '$' + (cents / 100).toFixed(2);
  };

  if (loading) {
    return (
      <div className="fund-loading">Loading club data...</div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="fund-error">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <h2>Fund Club</h2>

      {/* Balance Cards */}
      <div className="balance-cards">
          <div className="balance-card kitty">
            <div className="balance-label">Kitty Balance</div>
            <div className="balance-amount">
              {formatCurrency(accounts?.kitty?.balance)}
            </div>
          </div>
          <div className="balance-card betting">
            <div className="balance-label">Betting Balance</div>
            <div className="balance-amount">
              {formatCurrency(accounts?.betting?.balance)}
            </div>
          </div>
        </div>

        {/* Payment Card */}
        <div className="card-section">
          <h3>Payment Card</h3>
          <CardCapture
            clubId={clubId}
            onSuccess={() => {}}
            onError={(err) => setError(err)}
          />
        </div>

        {/* Tabs */}
        <div className="fund-tabs">
          <button
            className={`fund-tab ${activeTab === 'deposit' ? 'active' : ''}`}
            onClick={() => setActiveTab('deposit')}
          >
            Deposit
          </button>
          <button
            className={`fund-tab ${activeTab === 'transfer' ? 'active' : ''}`}
            onClick={() => setActiveTab('transfer')}
          >
            Transfer
          </button>
        </div>

        {/* Deposit Form */}
        {activeTab === 'deposit' && (
          <form onSubmit={handleDeposit} className="fund-form">
            <div className="form-group">
              <label>Deposit To</label>
              <select
                value={depositWallet}
                onChange={(e) => setDepositWallet(e.target.value)}
              >
                <option value="kitty">Kitty Account</option>
                <option value="betting">Betting Account</option>
              </select>
            </div>

            <div className="form-group">
              <label>Amount ($)</label>
              <div className="amount-input-group">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="0.00"
                  min="1"
                  max="10000"
                  step="0.01"
                  required
                />
              </div>
            </div>

            <div className="quick-amounts">
              {[10, 20, 50, 100, 200, 500].map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className="quick-amount-btn"
                  onClick={() => setDepositAmount(String(amt))}
                >
                  ${amt}
                </button>
              ))}
            </div>

            {depositResult && (
              <div className={`result-message ${depositResult.success ? 'success' : 'error'}`}>
                {depositResult.message}
              </div>
            )}

            <button type="submit" className="btn-submit" disabled={depositing || !depositAmount}>
              {depositing ? 'Processing...' : `Deposit $${depositAmount || '0.00'}`}
            </button>
          </form>
        )}

        {/* Transfer Form */}
        {activeTab === 'transfer' && (
          <form onSubmit={handleTransfer} className="fund-form">
            <div className="form-row">
              <div className="form-group">
                <label>From</label>
                <select
                  value={transferFrom}
                  onChange={(e) => {
                    setTransferFrom(e.target.value);
                    setTransferTo(e.target.value === 'kitty' ? 'betting' : 'kitty');
                  }}
                >
                  <option value="kitty">Kitty</option>
                  <option value="betting">Betting</option>
                </select>
              </div>
              <div className="transfer-arrow">&rarr;</div>
              <div className="form-group">
                <label>To</label>
                <select
                  value={transferTo}
                  onChange={(e) => {
                    setTransferTo(e.target.value);
                    setTransferFrom(e.target.value === 'kitty' ? 'betting' : 'kitty');
                  }}
                >
                  <option value="betting">Betting</option>
                  <option value="kitty">Kitty</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Amount ($)</label>
              <div className="amount-input-group">
                <span className="currency-symbol">$</span>
                <input
                  type="number"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                  placeholder="0.00"
                  min="1"
                  step="0.01"
                  required
                />
              </div>
            </div>

            {transferResult && (
              <div className={`result-message ${transferResult.success ? 'success' : 'error'}`}>
                {transferResult.message}
              </div>
            )}

            <button type="submit" className="btn-submit" disabled={transferring || !transferAmount}>
              {transferring ? 'Processing...' : `Transfer $${transferAmount || '0.00'}`}
            </button>
          </form>
        )}

        {/* Recent Transactions */}
        <div className="recent-transactions">
          <h3>Recent Transactions</h3>
          {accounts?.kitty?.transactions?.length > 0 || accounts?.betting?.transactions?.length > 0 ? (
            <div className="transaction-list">
              {[...(accounts?.kitty?.transactions || []), ...(accounts?.betting?.transactions || [])]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .slice(0, 10)
                .map((tx, i) => (
                  <div key={tx.id || i} className="transaction-row">
                    <div className="tx-info">
                      <span className="tx-description">{tx.description}</span>
                      <span className="tx-date">
                        {new Date(tx.date).toLocaleDateString()}
                      </span>
                    </div>
                    <span className={`tx-amount ${tx.amount >= 0 ? 'positive' : 'negative'}`}>
                      {tx.amount >= 0 ? '+' : ''}
                      {formatCurrency(Math.abs(tx.amount))}
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <p className="no-transactions">No transactions yet</p>
          )}
      </div>
    </div>
  );
}

export default ClubFund;
